import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { subscriptionsAPI, invoicesAPI } from '../../services/api'
import ChangelogCard from '../../components/common/ChangelogCard'

import { logger } from '@/utils/logger'

const statusTag: Record<string, { className: string; label: string }> = {
  ACTIVE: { className: 'wc-tag-accent', label: 'Aktiv' },
  TRIAL: { className: 'wc-tag-outline', label: 'Testversion' },
  CANCELLED: { className: 'wc-tag-neutral', label: 'Gekündigt' },
  CANCELLED_PENDING: { className: 'wc-tag-neutral', label: 'Kündigung beantragt' },
  EXPIRED: { className: 'wc-tag-neutral', label: 'Abgelaufen' },
}

const invoiceStatusTag: Record<string, { className: string; label: string }> = {
  NEW: { className: 'wc-tag-outline', label: 'Neu' },
  DUE: { className: 'wc-tag-outline', label: 'Fällig' },
  OVERDUE: { className: 'wc-tag-neutral', label: 'Überfällig' },
  REMINDED: { className: 'wc-tag-outline', label: 'Angemahnt' },
  PAID: { className: 'wc-tag-accent', label: 'Bezahlt' },
}

function formatDate(dateString?: string) {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('de-DE')
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount)
}

export default function CustomerDashboard() {
  const [subscriptions, setSubscriptions] = useState<any[]>([])
  const [invoices, setInvoices] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [subsRes, invoicesRes] = await Promise.all([
        subscriptionsAPI.getMy(),
        invoicesAPI.getMy(),
      ])
      setSubscriptions(subsRes.data)
      setInvoices(invoicesRes.data)
    } catch (error) {
      logger.error('Error loading dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const activeCount = subscriptions.filter((s) => s.status === 'ACTIVE').length
  const trialCount = subscriptions.filter((s) => s.status === 'TRIAL').length
  const openInvoicesCount = invoices.filter((i) => ['NEW', 'DUE', 'OVERDUE', 'REMINDED'].includes(i.status)).length
  const currentYear = new Date().getFullYear()
  const yearTotal = invoices
    .filter((i) => new Date(i.issue_date).getFullYear() === currentYear)
    .reduce((sum, i) => sum + i.amount, 0)

  const recentInvoices = [...invoices]
    .sort((a, b) => new Date(b.issue_date).getTime() - new Date(a.issue_date).getTime())
    .slice(0, 3)

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <div className="wc-spinner" />
      </div>
    )
  }

  return (
    <div>
      <h1 style={{ fontSize: 26, margin: '0 0 6px' }}>Willkommen zurück!</h1>
      <p className="wc-muted" style={{ margin: '0 0 32px', fontSize: 14 }}>
        Stand: {new Date().toLocaleDateString('de-DE')} · {subscriptions.length} Abonnement{subscriptions.length === 1 ? '' : 's'}
      </p>

      {/* Stat cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 16,
          marginBottom: 32,
        }}
      >
        <div className="wc-card wc-elev-sm" style={{ gap: 6 }}>
          <span className="wc-muted" style={{ fontSize: 13 }}>Aktive Apps</span>
          <span style={{ fontSize: 28 }}>{activeCount}</span>
        </div>
        <div className="wc-card wc-elev-sm" style={{ gap: 6 }}>
          <span className="wc-muted" style={{ fontSize: 13 }}>Testversionen</span>
          <span style={{ fontSize: 28, color: 'var(--wc-accent-300)' }}>{trialCount}</span>
        </div>
        <div className="wc-card wc-elev-sm" style={{ gap: 6 }}>
          <span className="wc-muted" style={{ fontSize: 13 }}>Offene Rechnungen</span>
          <span style={{ fontSize: 28 }}>{openInvoicesCount}</span>
        </div>
        <div className="wc-card wc-elev-sm" style={{ gap: 6 }}>
          <span className="wc-muted" style={{ fontSize: 13 }}>Gesamt {currentYear}</span>
          <span style={{ fontSize: 28 }}>{formatCurrency(yearTotal)}</span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)', gap: 24, alignItems: 'start' }}>
        {/* Subscriptions */}
        <div className="wc-card wc-elev-sm" style={{ gap: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 className="wc-card-title" style={{ margin: 0 }}>Meine Abonnements</h3>
            <Link to="/customer/apps" style={{ fontSize: 13 }}>Alle anzeigen →</Link>
          </div>

          {subscriptions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <p className="wc-muted" style={{ marginBottom: 16 }}>Sie haben noch keine Abonnements</p>
              <Link to="/apps" className="wc-btn wc-btn-primary">Apps entdecken</Link>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {subscriptions.map((sub) => {
                const tag = statusTag[sub.status] || statusTag.ACTIVE
                return (
                  <div
                    key={sub.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 14,
                      border: '1px solid var(--wc-divider)',
                      borderRadius: 'var(--wc-radius-md)',
                      opacity: sub.status === 'CANCELLED' || sub.status === 'EXPIRED' ? 0.6 : 1,
                      gap: 12,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14 }}>{sub.app?.name || 'Unbekannte App'}</div>
                      <div className="wc-muted" style={{ fontSize: 12 }}>
                        {sub.app_plan?.name || (sub.plan === 'YEARLY' ? 'Jährlich' : 'Monatlich')}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                      {sub.app?.login_url && (sub.status === 'ACTIVE' || sub.status === 'TRIAL') && (
                        <a
                          href={sub.app.login_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="wc-btn wc-btn-secondary"
                          style={{ padding: '4px 12px', fontSize: 12 }}
                        >
                          Zur App
                        </a>
                      )}
                      <span className={`wc-tag ${tag.className}`}>{tag.label}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div className="wc-card wc-elev-sm" style={{ gap: 14 }}>
            <h3 className="wc-card-title" style={{ margin: 0 }}>Letzte Rechnungen</h3>
            {recentInvoices.length === 0 ? (
              <p className="wc-muted" style={{ fontSize: 13, margin: 0 }}>Noch keine Rechnungen vorhanden.</p>
            ) : (
              <div>
                {recentInvoices.map((invoice) => {
                  const tag = invoiceStatusTag[invoice.status] || invoiceStatusTag.NEW
                  return (
                    <div key={invoice.id} className="wc-row">
                      <div>
                        <div style={{ fontSize: 13 }}>{invoice.invoice_number}</div>
                        <div className="wc-muted" style={{ fontSize: 12 }}>{formatDate(invoice.issue_date)}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13 }}>{formatCurrency(invoice.amount)}</div>
                        <span className={`wc-tag ${tag.className}`} style={{ marginTop: 2 }}>{tag.label}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            <Link to="/customer/invoices" style={{ fontSize: 13 }}>Alle Rechnungen →</Link>
          </div>

          <div className="wc-card wc-elev-sm" style={{ gap: 12 }}>
            <h3 className="wc-card-title" style={{ margin: 0 }}>Support</h3>
            <p className="wc-card-body" style={{ margin: 0 }}>
              Fragen zu Ihrem Abo oder ein technisches Problem? Wir helfen gerne weiter.
            </p>
            <Link to="/customer/support" className="wc-btn wc-btn-ghost" style={{ alignSelf: 'flex-start', padding: 0 }}>
              Support kontaktieren →
            </Link>
          </div>

          <ChangelogCard />
        </div>
      </div>
    </div>
  )
}
